/**
 * ScoreCalculator - Scoring rules service
 * Handles all scoring calculations and bonuses
 */

import { GameState, WordResult } from '../types/GameTypes';

export class ScoreCalculator {
  
  /**
   * Calculate score for a completed game
   * Based on original scoring: letterGuessesLeft when you win, 0 when you lose
   * 
   * @param gameState - The completed game state
   * @returns The calculated score
   */
  static calculateGameScore(gameState: GameState): number {
    if (gameState.gameWon) {
      return gameState.letterGuessesLeft;
    }
    return 0;
  }

  /**
   * Calculate score for a word result in daily mode
   * This matches the original WordResult scoring logic
   * 
   * @param letterGuessesLeft - Remaining letter guesses when completed
   * @param gameWon - Whether the game was won
   * @returns The score for this word
   */
  static calculateWordScore(letterGuessesLeft: number, gameWon: boolean): number {
    if (gameWon) {
      return letterGuessesLeft;
    }
    return 0;
  }

  /**
   * Calculate total daily score from all word results
   * 
   * @param wordResults - Array of completed word results
   * @returns Total score across all words
   */
  static calculateDailyTotal(wordResults: WordResult[]): number {
    return wordResults.reduce((total, result) => total + result.score, 0);
  }

  /**
   * Calculate streak score by adding a daily score to existing streak
   * This matches the original PersistentState streak score calculation
   * 
   * @param currentStreakScore - Current streak score
   * @param dailyScore - Score from today's daily challenge
   * @returns New streak score
   */
  static calculateStreakScore(currentStreakScore: number, dailyScore: number): number {
    return currentStreakScore + dailyScore;
  }

  /**
   * Check if a score qualifies for a perfect game
   * Perfect game = solved with maximum guesses remaining
   * 
   * @param score - The game score
   * @param maxPossibleScore - Maximum possible score (MAX_LETTER_GUESSES)
   * @returns True if perfect score
   */
  static isPerfectScore(score: number, maxPossibleScore: number): boolean {
    return score === maxPossibleScore;
  }
}
